import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, X } from 'lucide-react';
import { API_URL, apiClient } from '../api/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const SetupProgressTracker = () => {
  const navigate = useNavigate();
  const { selectedShopId } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [currentShop, setCurrentShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFirstTime, setIsFirstTime] = useState(false);

  useEffect(() => {
    if (selectedShopId) {
      fetchShopProgress();
    } else {
      setLoading(false);
      setCurrentShop(null);
    }
  }, [selectedShopId]);

  // Auto-show modal for new users
  useEffect(() => {
    const hasSeenSetup = localStorage.getItem('hasSeenSetupModal');
    if (!hasSeenSetup && currentShop && !loading) {
      const progress = calculateProgress();
      if (progress < 100) {
        setIsFirstTime(true);
        setShowModal(true);
        localStorage.setItem('hasSeenSetupModal', 'true');
      }
    }
  }, [currentShop, loading]);

  const fetchShopProgress = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('userId');
      if (!userId || !selectedShopId) {
        setLoading(false);
        return;
      }

      // Fetch all shops to find the selected one
      const response = await apiClient.get(`/api/shops/get?userId=${userId}`);

      if (response.status === 200) {
        const result = response.data;
        const shops = result.shops || [];
        
        // Find the currently selected shop
        const shop = shops.find(s => s._id === selectedShopId);
        setCurrentShop(shop || null);
      }
    } catch (error) {
      console.error('Error fetching shop progress:', error);
      setCurrentShop(null);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = () => {
    if (!currentShop) return 0;
    
    // Use setup_percentage from backend if available, otherwise fallback to legacy logic
    if (typeof currentShop.setup_percentage === 'number') {
      return currentShop.setup_percentage;
    }

    const steps = [
      currentShop.shopName ? true : false,
      currentShop.linkSuccess === true,
      currentShop.activationStatus?.toLowerCase() === 'activated'
    ];
    
    const completed = steps.filter(Boolean).length;
    return (completed / steps.length) * 100;
  };

  const progress = calculateProgress();
  const isComplete = progress === 100;

  // Show loading state
  if (loading) {
    return (
      <div
        style={{
          padding: '12px 16px',
          marginBottom: '8px',
          borderRadius: '12px',
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <div style={{ fontSize: '13px', fontWeight: '600', color: '#9ca3af' }}>
          Loading setup...
        </div>
      </div>
    );
  }

  // Don't show if setup is complete
  if (currentShop && isComplete) {
    return null;
  }

  // Handle case when no shop exists
  const noShop = !currentShop;

  const steps = noShop ? [
    {
      id: 1,
      title: 'Create Your First Shop',
      description: 'Add a new shop to get started with shopOS',
      completed: false,
      link: '/myshops'
    },
    {
      id: 2,
      title: 'Link as Subaccount',
      description: 'Add the shop to your main account as a subaccount',
      completed: false,
      link: '/myshops'
    },
    {
      id: 3,
      title: 'Activate Subaccount',
      description: 'Complete the activation process for your shop',
      completed: false,
      link: '/myshops'
    }
  ] : [
    {
      id: 1,
      title: 'Shop Created',
      description: `${currentShop.shopName} has been added to your account`,
      completed: true,
      link: '/myshops'
    },
    {
      id: 2,
      title: 'Link as Subaccount',
      description: 'Add this shop to your main account as a subaccount',
      completed: (currentShop.setup_percentage >= 66) || (currentShop.linkSuccess === true),
      link: '/myshops'
    },
    {
      id: 3,
      title: 'Activate Subaccount',
      description: 'Complete the activation process for this shop',
      completed: (currentShop.setup_percentage === 100) || (currentShop.activationStatus?.toLowerCase() === 'activated'),
      link: '/myshops'
    }
  ];

  return (
    <>
      {/* Progress Container */}
      <div
        style={{
          padding: '16px',
          marginBottom: '12px',
          borderRadius: '16px',
          background: 'rgba(20, 20, 20, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
        }}
      >
        {/* Header with Title and Percentage */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '12px' 
        }}>
          <span style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: '#ffffff' 
          }}>
            Account Setup
          </span>
          <span style={{ 
            fontSize: '15px', 
            fontWeight: '600', 
            color: '#9ca3af' 
          }}>
            {Math.round(progress)}%
          </span>
        </div>
        
        {/* Progress Bar */}
        <div style={{
          width: '100%',
          height: '8px',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          borderRadius: '10px',
          overflow: 'hidden',
          marginBottom: '12px'
        }}>
          <div style={{
            width: `${progress}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #ffffff 0%, #e5e5e5 100%)',
            borderRadius: '10px',
            transition: 'width 0.3s ease'
          }} />
        </div>

        {/* Complete Setup Button */}
        <button
          onClick={() => setShowModal(true)}
          style={{
            width: '100%',
            padding: '12px',
            background: 'linear-gradient(92deg, #bd4568 0%, #aa644d 100%)',
            border: 'none',
            borderRadius: '12px',
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(236, 72, 153, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(236, 72, 153, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(236, 72, 153, 0.3)';
          }}
        >
          Complete Setup
        </button>
      </div>

      {/* Setup Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              backgroundColor: '#0a0a0a',
              borderRadius: '24px',
              maxWidth: '600px',
              width: '100%',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              position: 'relative',
              overflow: 'hidden',
              padding: '32px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Horizontal Step Indicator */}
            <div style={{ marginBottom: '32px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                position: 'relative',
                marginBottom: '16px'
              }}>
                {/* Connecting Line */}
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  left: '16%',
                  right: '16%',
                  height: '2px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  zIndex: 0
                }} />

                {/* Step 1 */}
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  position: 'relative',
                  zIndex: 1
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: steps[0]?.completed 
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#ffffff',
                    marginBottom: '12px',
                    border: '3px solid #0a0a0a'
                  }}>
                    {steps[0]?.completed ? '✓' : '1'}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: '600', 
                      color: '#ffffff',
                      marginBottom: '4px'
                    }}>
                      {noShop ? 'Add shop' : 'Shop Created'}
                    </div>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#9ca3af',
                      maxWidth: '120px'
                    }}>
                      {noShop ? 'Create your first shop' : 'Shop added successfully'}
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  position: 'relative',
                  zIndex: 1
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: steps[1]?.completed 
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : 'rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: '700',
                    color: steps[1]?.completed ? '#ffffff' : '#6b7280',
                    marginBottom: '12px',
                    border: '3px solid #0a0a0a'
                  }}>
                    {steps[1]?.completed ? '✓' : '2'}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: '600', 
                      color: steps[1]?.completed ? '#ffffff' : '#9ca3af',
                      marginBottom: '4px'
                    }}>
                      Add Admin Access
                    </div>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#9ca3af',
                      maxWidth: '120px'
                    }}>
                      Link as subaccount
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  position: 'relative',
                  zIndex: 1
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: steps[2]?.completed 
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : 'rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: '700',
                    color: steps[2]?.completed ? '#ffffff' : '#6b7280',
                    marginBottom: '12px',
                    border: '3px solid #0a0a0a'
                  }}>
                    {steps[2]?.completed ? '✓' : '3'}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: '600', 
                      color: steps[2]?.completed ? '#ffffff' : '#9ca3af',
                      marginBottom: '4px'
                    }}>
                      Activate your shop
                    </div>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#9ca3af',
                      maxWidth: '120px'
                    }}>
                      to start tracking
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Video/Image Area */}
            <div style={{
              width: '100%',
              aspectRatio: '16/9',
              backgroundColor: '#1a1a1a',
              borderRadius: '16px',
              overflow: 'hidden',
              position: 'relative',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
            }}>
              {/* Placeholder Content */}
              <div style={{
                textAlign: 'center',
                color: '#6b7280',
                padding: '40px'
              }}>
                <div style={{
                  fontSize: '48px',
                  marginBottom: '16px'
                }}>
                  🎥
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#9ca3af',
                  marginBottom: '8px'
                }}>
                  Setup Guide Video
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#6b7280'
                }}>
                  Video will be added here
                </div>
              </div>
              
              {/* Play Button Overlay */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)';
                e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
              }}
              >
                <div style={{
                  width: 0,
                  height: 0,
                  borderLeft: '16px solid #0a0a0a',
                  borderTop: '10px solid transparent',
                  borderBottom: '10px solid transparent',
                  marginLeft: '4px'
                }} />
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              width: '100%'
            }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  background: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  navigate('/myshops');
                }}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  background: 'linear-gradient(92deg, #bd4568 0%, #aa644d 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(236, 72, 153, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Go Add Shop
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SetupProgressTracker;
